import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import api from '../utils/api';
import { toast } from 'sonner';

const EditBatchModal = ({ open, onClose, batch, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    capacity: '',
    start_time: '',
    end_time: ''
  });
  const [applyToFuture, setApplyToFuture] = useState(true);
  const [applyToAllTimes, setApplyToAllTimes] = useState(false);

  useEffect(() => {
    if (batch) {
      setFormData({
        capacity: batch.capacity || batch.max_capacity || '',
        start_time: batch.start_time || '',
        end_time: batch.end_time || ''
      });
    }
  }, [batch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.capacity || !formData.start_time || !formData.end_time) {
      toast.error('All fields are required');
      return;
    }

    // Validate time format
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (!timeRegex.test(formData.start_time) || !timeRegex.test(formData.end_time)) {
      toast.error('Invalid time format. Use HH:MM (24-hour format)');
      return;
    }

    try {
      setLoading(true);
      const res = await api.put(`/batches/${batch.batch_id}`, {
        capacity: parseInt(formData.capacity),
        start_time: formData.start_time,
        end_time: formData.end_time,
        apply_to_future: applyToFuture,
        apply_to_all_times: applyToFuture && applyToAllTimes
      });
      
      const n = res.data?.future_batches_updated || 0;
      toast.success(applyToFuture ? `Capacity saved for all future days${n ? ` (${n} batches updated)` : ''}` : 'Batch updated successfully');
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update batch');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-[#2C4C3B]">Edit Batch</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="capacity">Capacity (Number of Beds)</Label>
            <Input
              id="capacity"
              type="number"
              min="1"
              max="20"
              value={formData.capacity}
              onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
              placeholder="e.g., 15"
              className="border-[#E5E5DF]"
            />
            <div className="mt-3 space-y-2 rounded-md border border-[#E5E5DF] bg-[#F9F9F8] p-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="apply-to-future-checkbox"
                  checked={applyToFuture}
                  onChange={(e) => setApplyToFuture(e.target.checked)}
                  className="mt-0.5 w-4 h-4 text-[#2C4C3B] rounded"
                />
                <span className="text-sm text-[#1C1C1A]">
                  <span className="font-medium">Apply to all future days</span>
                  <span className="block text-xs text-[#1C1C1A]/60">Makes this the permanent capacity for the {batch?.start_time} {batch?.batch_type === 'premium' ? 'premium' : 'regular'} batch</span>
                </span>
              </label>
              {applyToFuture && (
                <label className="flex items-start gap-2 cursor-pointer pl-6">
                  <input
                    type="checkbox"
                    data-testid="apply-to-all-times-checkbox"
                    checked={applyToAllTimes}
                    onChange={(e) => setApplyToAllTimes(e.target.checked)}
                    className="mt-0.5 w-4 h-4 text-[#2C4C3B] rounded"
                  />
                  <span className="text-sm text-[#1C1C1A]">
                    <span className="font-medium">Also apply to every batch time</span>
                    <span className="block text-xs text-[#1C1C1A]/60">All {batch?.batch_type === 'premium' ? 'premium' : 'regular'} batches in this branch will use this capacity</span>
                  </span>
                </label>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="start_time">Start Time (24-hour format)</Label>
            <Input
              id="start_time"
              type="text"
              value={formData.start_time}
              onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
              placeholder="e.g., 07:40"
              className="border-[#E5E5DF]"
            />
            <p className="text-xs text-[#1C1C1A]/60">Format: HH:MM (e.g., 07:40, 14:30)</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="end_time">End Time (24-hour format)</Label>
            <Input
              id="end_time"
              type="text"
              value={formData.end_time}
              onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
              placeholder="e.g., 08:20"
              className="border-[#E5E5DF]"
            />
            <p className="text-xs text-[#1C1C1A]/60">Format: HH:MM (e.g., 08:20, 15:10)</p>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-[#2C4C3B] hover:bg-[#1e3429]"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditBatchModal;
